import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { parentsApi } from '@/api/endpoints/parents'
import Spinner from '@/components/common/Spinner'

interface PendingParent {
  id: string
  first_name?: string
  last_name?: string
  email: string
}

export default function ApprovalsAccordion() {
  const { user } = useAuth()
  const [open, setOpen] = useState(true)
  const [pending, setPending] = useState<PendingParent[]>([])
  const [loading, setLoading] = useState(false)
  const [actionBusy, setActionBusy] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    const run = async () => {
      if (!user?.parish_id) return
      setLoading(true)
      try {
        const data = await parentsApi.listPending()
        if (mounted) setPending(Array.isArray(data) ? (data as PendingParent[]) : [])
      } catch {
        if (mounted) setPending([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    run()
    return () => { mounted = false }
  }, [user?.parish_id])

  return (
    <div className="mt-6">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-white shadow-sm transition-transform duration-200 hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
      >
        <div className="text-left">
          <div className="text-lg font-semibold">Pending Approvals</div>
          <div className="text-xs text-gray-600">Review and approve new parent registrations</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 border border-gray-200">{pending.length}</span>
          <span className={`transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'tween', duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="p-4 rounded-b-xl border border-t-0 border-gray-200 bg-white" aria-busy={loading}>
              {loading && <Spinner label="Loading pending approvals…" />}
              {!loading && (
                pending.length === 0 ? (
                  <div className="text-sm text-gray-500">No pending approvals.</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    <div className="pb-2 flex items-center gap-2">
                      <button
                        className="btn-secondary text-xs"
                        disabled={pending.length === 0 || !!actionBusy}
                        onClick={async () => {
                          // Placeholder bulk reject: optimistic remove all, then try serial calls
                          const ids = pending.map(p => p.id)
                          setPending([])
                          for (const id of ids) {
                            try { await parentsApi.reject(id) } catch { /* swallow */ }
                          }
                        }}
                        title="Reject all pending parent registrations. Rejected parents will not be able to access the system."
                      >
                        Reject All
                      </button>
                      <button
                        className="btn-primary text-xs"
                        disabled={pending.length === 0 || !!actionBusy}
                        onClick={async () => {
                          const ids = pending.map(p => p.id)
                          setPending([])
                          for (const id of ids) {
                            try { await parentsApi.approve(id) } catch { /* swallow */ }
                          }
                        }}
                        title="Approve all pending parent registrations. Approved parents will receive an email and can log in."
                      >
                        Approve All
                      </button>
                    </div>
                    {pending.map(p => (
                      <div key={p.id} className="py-3 flex items-center justify-between">
                        <div>
                          <div className="font-medium">{p.first_name} {p.last_name}</div>
                          <div className="text-xs text-gray-600">{p.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="btn-secondary text-xs"
                            disabled={actionBusy === p.id}
                            onClick={async () => {
                              setActionBusy(p.id)
                              try {
                                setPending((cur) => cur.filter(x => x.id !== p.id))
                                await parentsApi.reject(p.id)
                              } finally {
                                setActionBusy(null)
                              }
                            }}
                            title="Reject this parent registration. They will not be able to access the system."
                          >
                            {actionBusy === p.id ? 'Working…' : 'Reject'}
                          </button>
                          <button
                            className="btn-primary text-xs"
                            disabled={actionBusy === p.id}
                            onClick={async () => {
                              setActionBusy(p.id)
                              try {
                                setPending((cur) => cur.filter(x => x.id !== p.id))
                                await parentsApi.approve(p.id)
                              } finally {
                                setActionBusy(null)
                              }
                            }}
                            title="Approve this parent registration. They will receive an email notification and can log in."
                          >
                            {actionBusy === p.id ? 'Working…' : 'Approve'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}


